<!--
 * COPYRIGHT Transtron Inc 2022
 *
 * 機能名：連携会社マスタ画面
 *
 * 作成者：2025/09/23 ノア）許
 * 最終修正者：2025/09/23 ノア）許
 *
 -->
<template>
  <div class="tti-main-view">
    <!-- 連携会社 -->
    <section>
      <nav class="tti-main-header-shadow">
        <div class="pull-left">
          <el-button class="backButton" @click="onBackButtonClick" v-tooltip="getTooltip(tooltipType.back)">
            <tti-svg-icon icon-name="wo_034"></tti-svg-icon>
          </el-button>
          <div class="tti-viewTitle">
            <tti-svg-icon icon-name="wo_008" v-if="isJumpedFromManageTarget === '1'"></tti-svg-icon>
            <span v-if="isJumpedFromManageTarget === '1'">{{
              $t('master.managementTargetMaster.viewTitle', [managementTargetShowName])
            }}</span>
            <tti-svg-icon icon-name="wo_035" v-if="isJumpedFromManageTarget === '1'"></tti-svg-icon>
            <tti-svg-icon icon-name="wo_008"></tti-svg-icon>
            <span>{{ $t('master.connectCompanyMaster.viewTitle') }}</span>
          </div>
        </div>
      </nav>
    </section>
    <!-- テーブル -->
    <section class="tti-padding-content">
      <div class="tti-content-title-container">
        <div class="tti-content-title">
          <span class="contentTitle">{{ $t('master.connectCompanyMaster.contentTitle') }}</span>
          <span>{{ tableData.length }}</span>
          <span class="viewTitle">{{ $t('master.viewTitle.subject') }}</span>
        </div>
        <div class="functionArea">
          <el-pagination
            background
            layout="prev, pager, next"
            :total="tableData.length"
            :page-size="visablePageSize"
            :hide-on-single-page="true"
            :current-page.sync="currentPage"
            @current-change="onCurrentPageChange"
          >
          </el-pagination>
          <div class="searchBox">
            <el-input v-model.trim="searchText" onkeyup="this.value=this.value.replace(/[,]/g,'')">
              <el-button slot="append" icon="el-icon-search" @click="onSearchBoxClick"></el-button>
            </el-input>
          </div>
        </div>
      </div>
      <el-form :model="form" ref="form" :rules="rules" :disabled="pageLock">
        <el-table
          ref="tableRef"
          :data="visableTableData"
          height="calc(100vh - 226px)"
          border
          highlight-current-row
          :row-class-name="tableRowClassName"
          :stripe="true"
          header-row-class-name="tti-master-table-header"
          :empty-text="$t('messages.common.emptyTableData')"
          @selection-change="handleSelectionChange"
          @sort-change="handleSortChange"
        >
          <el-table-column type="selection" width="35"></el-table-column>
          <!-- No -->
          <el-table-column type="index" :label="$t('master.connectCompanyMaster.no')" width="50" align="center">
          </el-table-column>
          <!-- 会社コード -->
          <el-table-column
            class-name="required"
            prop="companyCode"
            :label="$t('master.connectCompanyMaster.companyCode')"
            width="200"
            :sortable="true"
          >
            <template slot-scope="scope">
              <el-form-item :prop="'tableData[' + scope.$index + '].companyCode'" :rules="rules.companyCode">
                <el-input
                  :id="'tableData[' + scope.$index + '].companyCode'"
                  v-model.trim="scope.row.companyCode"
                  size="small"
                  @change="onInputChange()"
                ></el-input>
              </el-form-item>
            </template>
          </el-table-column>
          <!-- 会社名 -->
          <el-table-column
            class-name="required"
            prop="companyName"
            :label="$t('master.connectCompanyMaster.companyName')"
            width="300"
            :sortable="true"
          >
            <template slot-scope="scope">
              <el-form-item :prop="'tableData[' + scope.$index + '].companyName'" :rules="rules.companyName">
                <el-input
                  :id="'tableData[' + scope.$index + '].companyName'"
                  v-model.trim="scope.row.companyName"
                  size="small"
                  @change="onInputChange()"
                ></el-input>
              </el-form-item>
            </template>
          </el-table-column>
          <!-- 電話番号 -->
          <el-table-column prop="tel" :label="$t('master.connectCompanyMaster.tel')" width="300" :sortable="true">
            <template slot-scope="scope">
              <el-form-item :prop="'tableData[' + scope.$index + '].tel'" :rules="rules.tel">
                <el-input
                  :id="'tableData[' + scope.$index + '].tel'"
                  v-model.trim="scope.row.tel"
                  size="small"
                  @change="onInputChange()"
                ></el-input>
              </el-form-item>
            </template>
          </el-table-column>
          <!-- 住所 -->
          <el-table-column
            prop="location"
            :label="$t('master.connectCompanyMaster.location')"
            width="300"
            :sortable="true"
          >
            <template slot-scope="scope">
              <el-form-item :prop="'tableData[' + scope.$index + '].location'" :rules="rules.location">
                <el-input
                  :id="'tableData[' + scope.$index + '].location'"
                  v-model.trim="scope.row.location"
                  size="small"
                  @change="onInputChange()"
                ></el-input>
              </el-form-item>
            </template>
          </el-table-column>
          <!-- 備考 -->
          <el-table-column prop="remark" :label="$t('master.connectCompanyMaster.remark')" width="500" :sortable="true">
            <template slot-scope="scope">
              <el-form-item :prop="'tableData[' + scope.$index + '].remark'" :rules="rules.remark">
                <el-input
                  :id="'tableData[' + scope.$index + '].remark'"
                  v-model.trim="scope.row.remark"
                  size="small"
                  @change="onInputChange()"
                ></el-input>
              </el-form-item>
            </template>
          </el-table-column>
        </el-table>
      </el-form>
    </section>
    <!-- フッター -->
    <section class="tti-master-bottom-area">
      <div class="tti-bottom-left-area">
        <!-- 取消 -->
        <el-button
          class="tti-btn-default tti-btn-001"
          @click="onCancelButtonClick"
          :disabled="pageLock"
          v-tooltip="getTooltip(tooltipType.cancel)"
          ><tti-svg-icon icon-name="su_005"></tti-svg-icon>{{ $t('buttons.cancel') }}</el-button
        >
        <!-- 削除 -->
        <el-button
          class="tti-btn-default tti-btn-005"
          @click="onDeleteButtonClick"
          :disabled="pageLock"
          v-tooltip="getTooltip(tooltipType.delete)"
          ><tti-svg-icon icon-name="su_013"></tti-svg-icon>{{ $t('buttons.delete') }}</el-button
        >
        <!-- コピー -->
        <el-button
          class="tti-btn-default tti-btn-005"
          @click="onCopyButtonClick"
          :disabled="pageLock"
          v-tooltip="getTooltip(tooltipType.copy)"
        >
          <tti-svg-icon icon-name="su_012"></tti-svg-icon>{{ $t('buttons.copy') }}
        </el-button>
        <!-- 追加 -->
        <el-button
          class="tti-btn-default tti-btn-005"
          @click="onInsertButtonClick"
          :disabled="pageLock"
          v-tooltip="getTooltip(tooltipType.insert)"
        >
          <tti-svg-icon icon-name="su_011"></tti-svg-icon>{{ $t('buttons.insert') }}
        </el-button>
        <!-- テンプレート -->
        <el-button
          class="tti-btn-default tti-btn-006"
          @click="onTamplateButtonClick"
          :disabled="pageLock"
          v-tooltip="getTooltip(tooltipType.template)"
        >
          {{ $t('buttons.template') }}
        </el-button>
      </div>
      <div class="tti-bottom-right-area">
        <!-- 入力 -->
        <el-button
          class="tti-btn-default tti-btn-001"
          @click="onInputButtonClick"
          :disabled="pageLock"
          v-tooltip="getTooltip(tooltipType.input)"
        >
          <tti-svg-icon icon-name="su_004"></tti-svg-icon>{{ $t('buttons.input') }}
        </el-button>
        <!-- 出力 -->
        <el-button
          class="tti-btn-default tti-btn-001"
          @click="onOutputButtonClick"
          :disabled="pageLock"
          v-tooltip="getTooltip(tooltipType.output)"
        >
          <tti-svg-icon icon-name="su_003"></tti-svg-icon>{{ $t('buttons.output') }}
        </el-button>
        <!-- 保存 -->
        <el-button
          class="tti-btn-default tti-btn-001"
          @click="onSaveButtonClick"
          :disabled="pageLock"
          v-tooltip="getTooltip(tooltipType.save)"
        >
          <tti-svg-icon icon-name="su_002"></tti-svg-icon>{{ $t('buttons.save') }}
        </el-button>
        <!-- file -->
        <input ref="inputFile" type="file" @change="onFileChange" class="fileInput" accept=".csv" />
      </div>
    </section>
  </div>
</template>

<script lang="ts">
import { Component, Ref } from 'vue-property-decorator'
import { Form, Table } from 'element-ui'

import MST_BASE, { FormItemType } from '@/views/master/MST_BASE.vue'
import { i18n } from '@/locales'
import {
  updateConnectCompanyMasterData,
  ConnectCompanyMasterCSVDownload,
  getConnectCompanyMasterData
} from '@/store/apis'
import { MasterData, ConnectCompanyMasterData } from '@/models'
import { RULES, VALID_PATTERN } from '@/service/validateService'
import { msgBoxService } from '@/commons/services/msgBoxService'
import HelperService from '@/service/helperService'
import { TemplateCode } from '@/commons/constants'

/**
 * 連携会社マスタ画面
 *
 * @class
 */
@Component({
  name: 'mst-connect-company'
})
export default class MST_CONNECT_COMPANY extends MST_BASE {
  /**
   * フォームのref
   */
  @Ref('form') readonly formRef!: Form

  @Ref('tableRef') readonly tableRef!: Table

  /**
   * 画面ID
   */
  pageId = 'MST_CONNECT_COMPANY'

  /**
   * 第一列の項目（Focus用）
   */
  firstField = 'companyCode'

  /**
   * 最先のCSVヘッダー
   */
  firstCsvHeader = i18n.t('master.csvHeader.MST_CONNECT_COMPANY').toString()

  /**
   * テンプレートコード
   */
  templateCode = TemplateCode.MST_CONNECT_COMPANY

  /**
   * 管理対象一覧からジャンプしてきたか
   */
  isJumpedFromManageTarget = this.$route.query.isJumpedFromManageTarget

  /**
   * 連携会社マスタ編集FORM
   */
  form = {
    tableData: this.tableData
  } as Record<string, ConnectCompanyMasterData[]>

  /**
   * 検証ルール
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  rules = {} as any

  /**
   * CSVヘッダーリスト
   */
  csvHeaders = ['companyCode', 'companyName', 'tel', 'location', 'remark']

  // eslint-disable-next-line max-lines-per-function
  initRule(): void {
    this.rules = {
      // 会社コード
      companyCode: [
        // 必須
        {
          required: true,
          message: i18n.t('vaildate.common.required', [i18n.t('master.connectCompanyMaster.companyCode')]).toString(),
          csvMessage: 'vaildate.csvFile.required',
          messageEnterPoint: [i18n.t('master.connectCompanyMaster.companyCode').toString()],
          trigger: 'blur'
        },
        // 半角数字
        {
          pattern: VALID_PATTERN[RULES.NUMERIC],
          message: i18n
            .t('vaildate.common.maxNumberLength', [i18n.t('master.connectCompanyMaster.companyCode'), 6])
            .toString(),
          csvMessage: 'vaildate.csvFile.number',
          trigger: 'blur'
        },
        {
          validRule: [
            // 6文字以内
            {
              rule: RULES.MAXLENGTH,
              number: 6,
              message: i18n
                .t('vaildate.common.maxlength', [i18n.t('master.connectCompanyMaster.companyCode'), 6])
                .toString(),
              csvMessage: 'vaildate.csvFile.maxlength',
              messageEnterPoint: [i18n.t('master.connectCompanyMaster.companyCode').toString(), 6]
            },
            // 関連重複
            {
              rule: RULES.REPEAT,
              tableData: this.tableData,
              tableColumn: 'companyCode',
              message: i18n.t('vaildate.common.repeat', [i18n.t('master.connectCompanyMaster.companyCode')]).toString(),
              csvMessage: 'vaildate.csvFile.repeat',
              messageEnterPoint: [i18n.t('master.connectCompanyMaster.companyCode')]
            }
          ],
          trigger: 'blur'
        }
      ],
      // 会社名
      companyName: [
        // 必須
        {
          required: true,
          message: i18n.t('vaildate.common.required', [i18n.t('master.connectCompanyMaster.companyName')]).toString(),
          csvMessage: 'vaildate.csvFile.required',
          messageEnterPoint: [i18n.t('master.connectCompanyMaster.companyName').toString()],
          trigger: 'blur'
        },
        {
          validRule: [
            // 50文字以内
            {
              rule: RULES.MAXLENGTH,
              number: 50,
              message: i18n
                .t('vaildate.common.maxlength', [i18n.t('master.connectCompanyMaster.companyName'), 50])
                .toString(),
              csvMessage: 'vaildate.csvFile.maxlength',
              messageEnterPoint: [i18n.t('master.connectCompanyMaster.companyName').toString(), 50]
            },
            // 関連重複
            {
              rule: RULES.REPEAT,
              tableData: this.tableData,
              tableColumn: 'companyName',
              message: i18n.t('vaildate.common.repeat', [i18n.t('master.connectCompanyMaster.companyName')]).toString(),
              csvMessage: 'vaildate.csvFile.repeat',
              messageEnterPoint: [i18n.t('master.connectCompanyMaster.companyName')]
            }
          ],
          trigger: 'blur'
        }
      ],
      // 連絡先（TEL）
      tel: [
        // 16文字
        {
          validRule: [
            {
              rule: RULES.MAXLENGTH,
              number: 16,
              message: i18n.t('vaildate.common.maxlength', [i18n.t('master.connectCompanyMaster.tel'), 16]).toString(),
              csvMessage: 'vaildate.csvFile.maxlength',
              messageEnterPoint: [i18n.t('master.connectCompanyMaster.tel').toString(), 16]
            }
          ],
          trigger: 'blur'
        },
        // TEL
        {
          pattern: VALID_PATTERN[RULES.TEL],
          message: i18n
            .t('vaildate.common.alphanumericAndSymbols', [i18n.t('master.connectCompanyMaster.tel')])
            .toString(),
          csvMessage: 'vaildate.csvFile.tel',
          messageEnterPoint: [i18n.t('master.connectCompanyMaster.tel').toString()],
          trigger: 'blur'
        }
      ],
      // 住所
      location: [
        {
          validRule: [
            // 50文字以内
            {
              rule: RULES.MAXLENGTH,
              number: 50,
              message: i18n
                .t('vaildate.common.maxlength', [i18n.t('master.connectCompanyMaster.location'), 50])
                .toString(),
              csvMessage: 'vaildate.csvFile.maxlength',
              messageEnterPoint: [i18n.t('master.connectCompanyMaster.location').toString(), 50]
            }
          ],
          trigger: 'blur'
        }
      ],
      // 備考
      remark: [
        {
          validRule: [
            // 255文字以内
            {
              rule: RULES.MAXLENGTH,
              number: 255,
              message: i18n
                .t('vaildate.common.maxlength', [i18n.t('master.connectCompanyMaster.remark'), 255])
                .toString(),
              csvMessage: 'vaildate.csvFile.maxlength',
              messageEnterPoint: [i18n.t('master.connectCompanyMaster.remark').toString(), 255]
            }
          ],
          trigger: 'blur'
        }
      ]
    }
  }

  /**
   * インスタンスがマウントされたちょうど後にフック処理
   *
   * @returns {void}
   */
  mounted(): void {
    this.$log.debug('trailerMaster > mounted')
    this.initFormTemplate()
    this.fetchTableData()
    this.checkPageLock()
    this.initFormRef(this.formRef)
    this.initTableRef(this.tableRef)
    this.initSearchTargets()
  }

  protected fetchTableDataBefore(): () => Promise<MasterData> {
    return getConnectCompanyMasterData
  }

  fetchTableDataAfter(): void {
    this.initRule()
    this.initValidate(this.rules)
    this.multipleSelection = []
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getCsvRule(): any {
    return this.rules
  }

  /**
   * テーブル項目を再チェックする
   *
   * @returns {void}
   */
  onInputChange(): void {
    this.$nextTick(() => {
      this.formRef.validate()
    })
  }

  /**
   * コピー前の処理。
   *
   * @param {Array<any>} multipleSelection コピー対象
   * @returns {void}
   */
  // eslint-disable-next-line
  beforeDataCopy(multipleSelection: Array<any>): void {
    multipleSelection.forEach(item => {
      delete item.hasManagementTarget
    })
  }

  /**
   * 削除前に、削除チェックを行う。
   *
   * @returns {boolean} チェック結果
   */
  deleteTableDataBefore(): boolean {
    let canDelete = true
    for (const data of this.multipleSelection) {
      if (data.hasManagementTarget) {
        canDelete = false
        msgBoxService.info(
          i18n
            .t('messages.masterCommon.cantDelete', [
              data.companyCode,
              i18n.t('master.connectCompanyMaster.managementMaster').toString()
            ])
            .toString()
        )
        break
      }
    }

    return canDelete
  }
  /**
   * 保存前に、チェックを行う
   *
   * @returns {Promise<boolean>} チェック結果
   */
  protected async saveTableDataBefore(): Promise<boolean> {
    try {
      const valid = await this.formRef.validate()
      if (!valid) {
        return false
      }

      return valid
    } catch {
      this.formRef.validate((valid, fields) => {
        if (!valid) {
          this.focusOnValidField(fields)
        }
      })
      return false
    }
  }

  /**
   * 保存API
   *
   * @returns {void}
   */
  async saveTableDataAPI(): Promise<void> {
    const params = this.getUpsertRequest()
    await updateConnectCompanyMasterData(params)
  }

  /**
   * 出力API
   *
   * @returns {void}
   */
  async tableDataOutputAPI(): Promise<void> {
    const res = await ConnectCompanyMasterCSVDownload()
    HelperService.download(res.fileName, res.data)
  }

  initSearchTargets(): void {
    this.searchTargets = [
      {
        type: FormItemType.Input,
        field: 'companyCode'
      },
      {
        type: FormItemType.Input,
        field: 'companyName'
      },
      {
        type: FormItemType.Input,
        field: 'tel'
      },
      {
        type: FormItemType.Input,
        field: 'location'
      },
      {
        type: FormItemType.Input,
        field: 'remark'
      }
    ]
  }

  /**
   * 入力FORMのテンプレートを初期化する
   *
   * @returns {void}
   */
  initFormTemplate(): void {
    this.formTemplate = {
      companyCode: '',
      companyName: '',
      tel: '',
      location: '',
      remark: ''
    }
  }
}
</script>

<style lang="scss" scoped>
::v-deep .cell {
  text-overflow: unset;
}

::v-deep .tti-bottom-right-area .tti-btn-default {
  margin-right: 0 !important;
}

.tti-viewTitle {
  margin-right: 20px;
}

.fileInput {
  display: none;
}
</style>
